import { useEffect, useMemo, useState } from "react";
import DashboardShell from "../components/shell/DashboardShell";
import api, { formatApiError } from "../lib/api";
import { downloadPdf, authenticatedDownload } from "../lib/pdf";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Download, Calendar, Printer, FileText, Package, Send, Building2, ListChecks, Layers, Landmark } from "lucide-react";
import { toast } from "sonner";
import SreoReport from "../components/reports/SreoReport";
import CombinedSreoRentRoll from "../components/reports/CombinedSreoRentRoll";
import USDateInput from "../components/USDateInput";
import LenderPackageButton from "../components/LenderPackageButton";
import { useAuth } from "../context/AuthContext";

const currency = (n) =>
  `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;

/** Convert an ISO 'YYYY-MM-DD' string into US-style 'MM/DD/YYYY'. Returns
 *  the input untouched on parse failure so we don't break rows with quirky
 *  values that crept in through legacy imports. */
const toUSDate = (iso) => {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
  return m ? `${m[2]}/${m[3]}/${m[1]}` : String(iso);
};

function firstOfMonth() {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
}
function today() {
  return new Date().toISOString().slice(0, 10);
}

export default function Reports() {
  const { user } = useAuth();
  const today_s = today();
  const start_s = `${new Date().getFullYear()}-01-01`;
  const [emailing, setEmailing] = useState(false);
  // Active paid subscription unlocks the Loan Portfolio tab + PDF. Admins
  // always pass so internal QA can validate the report.
  const canSeeLoanPortfolio =
    user?.role === "admin" || user?.subscription_status === "active";

  const downloadBundle = () => {
    downloadPdf(
      "/api/reports/bundle",
      `report-bundle_${start_s}_to_${today_s}.zip`,
      { start_date: start_s, end_date: today_s },
    ).catch(() => toast.error("Bundle download failed"));
  };

  const downloadSnapshot = () => {
    authenticatedDownload(
      "/api/reports/portfolio-snapshot/pdf",
      `portfolio-snapshot_${today_s}.pdf`,
    ).catch(() => toast.error("Snapshot download failed"));
  };

  const emailBundle = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post(`/reports/bundle/email`, null, {
        params: { start_date: start_s, end_date: today_s },
      });
      const res = data.result || {};
      const recipients = data.recipients || [];
      if (res.status === "sent") {
        toast.success(`Bundle emailed to ${recipients.length} recipient(s)`, {
          description: recipients.join(", "),
        });
      } else if (res.status === "mocked") {
        toast.message("Email in MOCK mode", {
          description: `Would send to: ${recipients.join(", ")}. Set RESEND_API_KEY to deliver.`,
        });
      } else if (res.status === "skipped") {
        toast.error("No recipients configured");
      } else {
        toast.error(res.error || "Email failed");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Email failed");
    } finally { setEmailing(false); }
  };

  return (
    <DashboardShell>
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="overline text-slate-500" data-testid="reports-company-overline">
              {user?.company ? `${user.company} · Reports` : "Reports"}
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{fontFamily:"Outfit"}}>Portfolio Reporting</h1>
            <p className="text-sm text-slate-500 mt-1">Drill into rent collections and debt service across your commercial assets.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={downloadSnapshot}
              data-testid="portfolio-snapshot-button"
              className="btn-outline-dark flex items-center gap-2 text-sm"
              title="One-page investor snapshot · purchase cost, market value, unrealized gain, weighted hold"
            >
              <Building2 className="w-4 h-4" /> Portfolio snapshot
            </button>
            <button
              onClick={emailBundle}
              disabled={emailing}
              data-testid="email-bundle-button"
              className="btn-outline-dark flex items-center gap-2 text-sm disabled:opacity-60"
              title="Email the YTD bundle to you and your stakeholder list"
            >
              <Send className="w-4 h-4" /> {emailing ? "Sending…" : "Email to stakeholders"}
            </button>
            <button
              onClick={downloadBundle}
              data-testid="report-bundle-button"
              className="btn-primary-dark flex items-center gap-2 text-sm"
              title={`Snapshot + rent roll + expenses + loan portfolio · ${start_s} → ${today_s}`}
            >
              <Package className="w-4 h-4" /> Download YTD bundle (.zip)
            </button>
          </div>
        </div>

        <Tabs defaultValue="rent-roll" className="w-full">
          <TabsList className="bg-white border border-slate-200 rounded-sm p-1 mb-6 flex-wrap h-auto gap-1">
            <TabsTrigger
              value="rent-roll"
              data-testid="tab-rent-roll"
              className="rounded-sm px-5 py-2 text-sm font-semibold border border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:border-blue-700 transition-colors"
            >
              Rent Roll
            </TabsTrigger>
            <TabsTrigger
              value="expenses"
              data-testid="tab-expenses"
              className="rounded-sm px-5 py-2 text-sm font-semibold border border-amber-300 bg-amber-100/70 text-amber-900 hover:bg-amber-100 data-[state=active]:bg-amber-800 data-[state=active]:text-white data-[state=active]:border-amber-900 transition-colors"
            >
              Property Expenses
            </TabsTrigger>
            {canSeeLoanPortfolio && (
              <TabsTrigger
                value="loan-portfolio"
                data-testid="tab-loan-portfolio"
                className="rounded-sm px-5 py-2 text-sm font-semibold border border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 data-[state=active]:bg-emerald-700 data-[state=active]:text-white data-[state=active]:border-emerald-800 transition-colors"
              >
                Loan Portfolio
              </TabsTrigger>
            )}
            <TabsTrigger
              value="taxes-due"
              data-testid="tab-taxes-due"
              className="rounded-sm px-5 py-2 text-sm font-semibold flex items-center gap-1.5 border border-slate-300 bg-slate-100 text-slate-900 hover:bg-slate-200 data-[state=active]:bg-slate-900 data-[state=active]:text-white data-[state=active]:border-black transition-colors"
            >
              <Landmark className="w-3.5 h-3.5" /> Taxes Due
            </TabsTrigger>
            <TabsTrigger
              value="sreo"
              data-testid="tab-sreo"
              className="rounded-sm px-5 py-2 text-sm font-semibold flex items-center gap-1.5 border border-red-200 bg-red-50 text-red-800 hover:bg-red-100 data-[state=active]:bg-red-700 data-[state=active]:text-white data-[state=active]:border-red-800 transition-colors"
            >
              <ListChecks className="w-3.5 h-3.5" /> Schedule of Real Estate Owned
            </TabsTrigger>
            <TabsTrigger
              value="combined"
              data-testid="tab-combined"
              className="rounded-sm px-5 py-2 text-sm font-semibold flex items-center gap-1.5 border border-orange-200 bg-orange-50 text-orange-800 hover:bg-orange-100 data-[state=active]:bg-orange-600 data-[state=active]:text-white data-[state=active]:border-orange-700 transition-colors"
            >
              <Layers className="w-3.5 h-3.5" /> Combined SREO + Rent Roll
            </TabsTrigger>
          </TabsList>

          <TabsContent value="rent-roll">
            <RentRollReport />
          </TabsContent>
          <TabsContent value="expenses">
            <ExpenseReport />
          </TabsContent>
          {canSeeLoanPortfolio && (
            <TabsContent value="loan-portfolio">
              <LoanPortfolioReport />
            </TabsContent>
          )}
          <TabsContent value="taxes-due">
            <TaxesDueReport />
          </TabsContent>
          <TabsContent value="sreo">
            <SreoReport companyName={user?.company || ""} companyLogo={user?.logo_data_uri || ""} />
          </TabsContent>
          <TabsContent value="combined">
            <CombinedSreoRentRoll companyName={user?.company || ""} companyLogo={user?.logo_data_uri || ""} />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardShell>
  );
}

/* ---------------- Expense Report ---------------- */
function ExpenseReport() {
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [propertyId, setPropertyId] = useState("");
  const [start, setStart] = useState(firstOfMonth());
  const [end, setEnd] = useState(today());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/properties").then((r) => setProperties(r.data));
  }, []);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/reports/expenses", {
        params: { property_id: propertyId || undefined, start_date: start, end_date: end },
      });
      setData(data);
    } finally { setBusy(false); }
  };

  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  const downloadPdfExpense = async () => {
    try {
      const params = { start_date: start, end_date: end };
      if (propertyId) params.property_id = propertyId;
      const slug = (properties.find(p => p.id === propertyId)?.name || "all-properties").replace(/\s+/g, "_");
      await authenticatedDownload(
        "/api/reports/expenses/pdf",
        `expense-report_${slug}_${start}_to_${end}.pdf`,
        params,
      );
    } catch (error) {
      console.error("Expense PDF download failed", error);
      alert("PDF download failed");
    }
  };

  const propertyDisplay = propertyId
    ? (properties.find(p => p.id === propertyId)?.name || "—")
    : "All properties";

  return (
    <div data-testid="expense-report">
      {/* Filter bar — hidden when printing */}
      <div className="bg-white border border-slate-200 rounded-sm p-5 mb-6 no-print">
        <div className="grid md:grid-cols-4 gap-4">
          <div>
            <label className="overline text-slate-600">Property</label>
            <select data-testid="expense-property-filter" className="input-clean mt-2 w-full" value={propertyId} onChange={(e) => setPropertyId(e.target.value)}>
              <option value="">All properties</option>
              {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="overline text-slate-600 flex items-center gap-1.5"><Calendar className="w-3 h-3" /> Start date</label>
            <USDateInput data-testid="expense-start-date" className="input-clean mt-2 w-full" value={start} onChange={(iso) => setStart(iso)} />
          </div>
          <div>
            <label className="overline text-slate-600 flex items-center gap-1.5"><Calendar className="w-3 h-3" /> End date</label>
            <USDateInput data-testid="expense-end-date" className="input-clean mt-2 w-full" value={end} onChange={(iso) => setEnd(iso)} />
          </div>
          <div className="flex items-end">
            <button onClick={run} disabled={busy} data-testid="generate-expense-report" className="btn-primary-dark w-full disabled:opacity-60">
              {busy ? "Generating…" : "Generate"}
            </button>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-slate-100">
          <button onClick={() => window.print()} data-testid="print-expense-report" className="btn-outline-dark flex items-center gap-2 text-sm">
            <Printer className="w-4 h-4" /> Print
          </button>
          <button onClick={downloadPdfExpense} data-testid="download-expense-pdf" disabled={!data} className="btn-primary-dark flex items-center gap-2 text-sm disabled:opacity-60">
            <Download className="w-4 h-4" /> Download PDF
          </button>
        </div>
      </div>

      {/* Printable / on-screen report body */}
      {data && (
        <div className="expense-print-root">
          <div className="print-header hidden print:block mb-6">
            {user?.logo_data_uri && (
              <img
                src={user.logo_data_uri}
                alt={user?.company || "Company logo"}
                className="h-12 w-auto object-contain mb-2"
                data-testid="expense-print-logo"
              />
            )}
            <div className="text-xs text-slate-500 uppercase tracking-widest font-bold">
              {user?.company || "RealtyVault Manager"}
            </div>
            {user?.company && (
              <div className="text-[10px] text-slate-400 uppercase tracking-widest mt-0.5">
                Prepared on RealtyVault Manager
              </div>
            )}
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 mt-1" style={{fontFamily:"Outfit"}}>Property Expense Report</h1>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm p-5 mb-6">
            <div className="flex flex-wrap justify-between items-start gap-4">
              <div>
                <div className="overline text-slate-500">Property</div>
                <div className="text-xl font-bold text-slate-900 mt-1" style={{fontFamily:"Outfit"}}>{propertyDisplay}</div>
                {data.property?.address && <div className="text-sm text-slate-500 mt-0.5">{data.property.address}</div>}
              </div>
              <div className="text-right">
                <div className="overline text-slate-500">Period</div>
                <div className="font-mono font-bold text-slate-900 mt-1">{data.start_date} → {data.end_date}</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Summary label="Total Expenses" value={currency(data.total)} bad={data.total > 0} />
            <Summary label="Line Items" value={data.expenses.length} />
            <Summary label="Categories" value={data.by_category.length} />
            <Summary label="Avg / Item" value={currency(data.expenses.length ? data.total / data.expenses.length : 0)} />
          </div>

          {data.by_category.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-sm mb-6 overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-200">
                <div className="overline text-slate-500">Breakdown</div>
                <div className="text-sm font-semibold text-slate-900">By category</div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left"><Th>Category</Th><Th className="text-right">Count</Th><Th className="text-right">Total</Th><Th className="text-right">% of Total</Th></tr>
                </thead>
                <tbody>
                  {data.by_category.map((c) => (
                    <tr key={c.category} className="border-b border-slate-100" data-testid={`expense-category-${c.category}`}>
                      <Td className="font-semibold text-slate-900">{c.category}</Td>
                      <Td className="text-right font-mono">{c.count}</Td>
                      <Td className="text-right font-mono font-bold">{currency(c.amount)}</Td>
                      <Td className="text-right font-mono text-slate-500">{data.total ? ((c.amount / data.total) * 100).toFixed(1) : "0.0"}%</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="overline text-slate-500">Detail</div>
                <div className="text-sm font-semibold text-slate-900">Line items</div>
              </div>
            </div>
            <table className="w-full text-sm" data-testid="expense-detail-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left">
                  <Th>Date</Th>
                  {!propertyId && <Th>Property</Th>}
                  <Th>Category</Th>
                  <Th>Description</Th>
                  <Th className="text-right">Amount</Th>
                </tr>
              </thead>
              <tbody>
                {data.expenses.length === 0 && (
                  <tr><td colSpan={propertyId ? 4 : 5} className="text-center py-16 text-slate-400">No expenses in this period.</td></tr>
                )}
                {data.expenses.map((e) => (
                  <tr key={e.id} className="border-b border-slate-100" data-testid={`expense-line-${e.id}`}>
                    <Td className="font-mono text-xs">{e.date}</Td>
                    {!propertyId && <Td className="font-semibold text-slate-900">{e.property_name}</Td>}
                    <Td><span className="overline text-slate-500">{e.category}</span></Td>
                    <Td className="text-slate-600">{e.description || "—"}</Td>
                    <Td className="text-right font-mono font-bold">{currency(e.amount)}</Td>
                  </tr>
                ))}
              </tbody>
              {data.expenses.length > 0 && (
                <tfoot>
                  <tr className="bg-slate-900 text-white">
                    <Td colSpan={propertyId ? 3 : 4} className="font-bold">Total</Td>
                    <Td className="text-right font-mono font-bold">{currency(data.total)}</Td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Rent Roll ---------------- */
function RentRollReport() {
  const [start, setStart] = useState(firstOfMonth());
  const [end, setEnd] = useState(today());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/reports/rent-roll", { params: { start_date: start, end_date: end } });
      setData(data);
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const exportCsv = () => {
    if (!data) return;
    const rows = [
      ["Property", "Unit", "Tenant", "Monthly Rent", "Expected", "Collected", "Outstanding", "Status"],
      ...data.rows.map((r) => [r.property_name, r.unit_label, r.tenant_name, r.monthly_rent, r.expected, r.collected, r.outstanding, r.outstanding <= 0 ? "Paid" : "Outstanding"]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rent-roll_${start}_to_${end}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="bg-white border border-slate-200 rounded-sm p-5 mb-6">
        <div className="flex flex-col md:flex-row md:items-end gap-4">
          <div>
            <label className="overline text-slate-600 flex items-center gap-1.5"><Calendar className="w-3 h-3" /> Start date</label>
            <USDateInput data-testid="report-start-date" className="input-clean mt-2 w-full md:w-56" value={start} onChange={(iso) => setStart(iso)} />
          </div>
          <div>
            <label className="overline text-slate-600 flex items-center gap-1.5"><Calendar className="w-3 h-3" /> End date</label>
            <USDateInput data-testid="report-end-date" className="input-clean mt-2 w-full md:w-56" value={end} onChange={(iso) => setEnd(iso)} />
          </div>
          <button onClick={load} disabled={busy} data-testid="generate-report-button" className="btn-primary-dark disabled:opacity-60">
            {busy ? "Generating…" : "Generate"}
          </button>
          <div className="flex gap-2 md:ml-auto">
            {[
              ["This month", firstOfMonth(), today()],
              ["Last 30 days", new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10), today()],
              ["YTD", `${new Date().getFullYear()}-01-01`, today()],
            ].map(([label, s, e]) => (
              <button key={label} onClick={() => { setStart(s); setEnd(e); setTimeout(load, 0); }} data-testid={`preset-${label.toLowerCase().replace(/\s/g, "-")}`} className="text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 px-3 py-1.5 rounded-sm transition-colors">{label}</button>
            ))}
          </div>
          <button onClick={exportCsv} disabled={!data} data-testid="export-csv-button" className="btn-outline-dark flex items-center gap-2 text-sm disabled:opacity-60">
            <Download className="w-4 h-4" /> CSV
          </button>
          <button
            onClick={() => downloadPdf("/api/reports/rent-roll/pdf", `rent-roll_${start}_to_${end}.pdf`, { start_date: start, end_date: end })}
            disabled={!data}
            data-testid="rent-roll-pdf-button"
            className="btn-primary-dark flex items-center gap-2 text-sm disabled:opacity-60"
          >
            <FileText className="w-4 h-4" /> PDF
          </button>
          <LenderPackageButton testid="reports-lender-package-btn" />
        </div>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="report-summary">
          <Summary label="Expected" value={currency(data.totals.expected)} />
          <Summary label="Collected" value={currency(data.totals.collected)} good />
          <Summary label="Outstanding" value={currency(data.totals.outstanding)} bad={data.totals.outstanding > 0} />
          <Summary label="Collection Rate" value={`${data.totals.collection_rate}%`} />
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="rent-roll-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <Th>Property</Th><Th>Unit</Th><Th>Tenant</Th>
              <Th className="text-right whitespace-nowrap">Monthly Rent</Th>
              <Th className="text-right">Expected</Th>
              <Th className="text-right">Collected</Th>
              <Th className="text-right">Outstanding</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {!data && <tr><td colSpan={8} className="text-center py-16 text-slate-400">Pick a date range and generate.</td></tr>}
            {data && data.rows.length === 0 && <tr><td colSpan={8} className="text-center py-16 text-slate-400">No active leases in this range.</td></tr>}
            {data?.rows.map((r) => (
              <tr key={r.lease_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`rent-roll-row-${r.lease_id}`}>
                <Td className="font-semibold text-slate-900">{r.property_name}</Td>
                <Td>{r.unit_label}</Td>
                <Td className="text-slate-600">{r.tenant_name}<div className="text-xs text-slate-400">{r.tenant_email}</div></Td>
                <Td className="text-right font-mono whitespace-nowrap">{currency(r.monthly_rent)}</Td>
                <Td className="text-right font-mono text-slate-600 whitespace-nowrap">{currency(r.expected)}</Td>
                <Td className="text-right font-mono font-bold text-slate-900 whitespace-nowrap">{currency(r.collected)}</Td>
                <Td className={`text-right font-mono font-bold whitespace-nowrap ${r.outstanding > 0 ? "text-red-600" : "text-green-600"}`}>{currency(r.outstanding)}</Td>
                <Td><span className={`overline ${r.outstanding <= 0 ? "text-green-600" : "text-amber-600"}`}>{r.outstanding <= 0 ? "Paid" : "Outstanding"}</span></Td>
              </tr>
            ))}
          </tbody>
          {data && data.rows.length > 0 && (
            <tfoot>
              <tr className="bg-slate-900 text-white">
                <Td colSpan={3} className="font-bold">Totals</Td>
                <Td className="text-right font-mono font-bold whitespace-nowrap">{currency(data.rows.reduce((s, r) => s + r.monthly_rent, 0))}</Td>
                <Td className="text-right font-mono font-bold whitespace-nowrap">{currency(data.totals.expected)}</Td>
                <Td className="text-right font-mono font-bold whitespace-nowrap">{currency(data.totals.collected)}</Td>
                <Td className="text-right font-mono font-bold whitespace-nowrap">{currency(data.totals.outstanding)}</Td>
                <Td>—</Td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

/* ---------------- Loan Portfolio ---------------- */
function LoanPortfolioReport() {
  const [loans, setLoans] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all");
  const [start, setStart] = useState(`${new Date().getFullYear() - 5}-01-01`);
  const [end, setEnd] = useState(today());

  useEffect(() => {
    api.get("/loans").then((r) => setLoans(r.data)).catch(() => setLoans([]));
  }, []);

  if (loans === null) {
    return <div className="text-slate-400 py-10 text-center">Loading…</div>;
  }

  const filtered = loans.filter((l) => {
    if (typeFilter !== "all" && (l.loan_type || "amortizing") !== typeFilter) return false;
    if (l.loan_date && (l.loan_date < start || l.loan_date > end)) return false;
    return true;
  });

  const totals = filtered.reduce(
    (acc, l) => ({
      principal: acc.principal + (l.loan_amount || 0),
      monthly: acc.monthly + (l.monthly_payment || 0),
      interest: acc.interest + (l.total_interest || 0),
      total_paid: acc.total_paid + (l.total_paid || 0),
      balloon: acc.balloon + (l.balloon_payment || 0),
      annual_debt_service: acc.annual_debt_service + ((l.monthly_payment || 0) * 12),
    }),
    { principal: 0, monthly: 0, interest: 0, total_paid: 0, balloon: 0, annual_debt_service: 0 }
  );

  const weightedRate = filtered.reduce((s, l) => s + (l.loan_amount * l.interest_rate), 0) / (totals.principal || 1);

  const exportCsv = () => {
    const rows = [
      ["Loan #", "Property Address", "Date", "Type", "Amount", "Rate (%)", "Term (yrs)", "IO Months", "Monthly Payment", "Total Interest", "Total Paid", "Balloon", "Lender"],
      ...filtered.map((l) => [
        l.loan_number, l.property_address, l.loan_date, l.loan_type || "amortizing",
        l.loan_amount, l.interest_rate, l.term_years, l.io_period_months || 0,
        l.monthly_payment, l.total_interest, l.total_paid, l.balloon_payment || 0, l.lender || "",
      ]),
      [],
      ["TOTALS", "", "", "", totals.principal, "", "", "", totals.monthly, totals.interest, totals.total_paid, totals.balloon, ""],
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `loan-portfolio_${today()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      {/* Filter bar */}
      <div className="bg-white border border-slate-200 rounded-sm p-5 mb-6 flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-end gap-4 flex-wrap">
          <div>
            <label className="overline text-slate-600">Loan type</label>
            <select data-testid="loan-type-filter" className="input-clean mt-2" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="all">All types</option>
              <option value="amortizing">Amortizing only</option>
              <option value="interest_only">Interest-only only</option>
            </select>
          </div>
          <div>
            <label className="overline text-slate-600 flex items-center gap-1.5"><Calendar className="w-3 h-3" /> Originated from</label>
            <USDateInput data-testid="loan-start-date" className="input-clean mt-2 w-44" value={start} onChange={(iso) => setStart(iso)} />
          </div>
          <div>
            <label className="overline text-slate-600 flex items-center gap-1.5"><Calendar className="w-3 h-3" /> Originated to</label>
            <USDateInput data-testid="loan-end-date" className="input-clean mt-2 w-44" value={end} onChange={(iso) => setEnd(iso)} />
          </div>
          <div className="text-xs text-slate-500 font-mono pb-3">
            Showing <span className="font-bold text-slate-900">{filtered.length}</span> of {loans.length} loans
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={exportCsv} disabled={!filtered.length} data-testid="export-loans-csv" className="btn-outline-dark flex items-center gap-2 text-sm disabled:opacity-60">
            <Download className="w-4 h-4" /> CSV
          </button>
          <button
            onClick={() => downloadPdf("/api/reports/loan-portfolio/pdf", `loan-portfolio_${start}_to_${end}.pdf`, { start_date: start, end_date: end })}
            disabled={!filtered.length}
            data-testid="loan-portfolio-pdf-button"
            className="btn-primary-dark flex items-center gap-2 text-sm disabled:opacity-60"
          >
            <FileText className="w-4 h-4" /> PDF
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="loan-report-summary">
        <Summary label="Total Principal" value={currency(totals.principal)} />
        <Summary label="Monthly Debt Service" value={currency(totals.monthly)} good />
        <Summary label="Annual Debt Service" value={currency(totals.annual_debt_service)} />
        <Summary label="Weighted Avg. Rate" value={`${weightedRate.toFixed(2)}%`} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <Summary label="Total Interest (life)" value={currency(totals.interest)} bad={totals.interest > 0} />
        <Summary label="Total Payments (life)" value={currency(totals.total_paid)} />
        <Summary label="Balloon Exposure" value={currency(totals.balloon)} bad={totals.balloon > 0} />
      </div>

      {/* Detail table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200">
          <div className="overline text-slate-500">Detail</div>
          <div className="text-sm text-slate-900 font-semibold">Loan-by-loan breakdown</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="loan-report-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <Th>Loan #</Th>
                <Th>Property Address</Th>
                <Th>Date</Th>
                <Th>Type</Th>
                <Th className="text-right">Amount</Th>
                <Th className="text-right">Rate</Th>
                <Th className="text-right">Term</Th>
                <Th className="text-right">Monthly</Th>
                <Th className="text-right">Total Interest</Th>
                <Th className="text-right">Total Paid</Th>
                <Th className="text-right">Balloon</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={11} className="text-center py-16 text-slate-400">No loans match this filter.</td></tr>
              )}
              {filtered.map((l) => (
                <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`loan-report-row-${l.id}`}>
                  <Td className="font-mono text-xs font-bold text-slate-900">{l.loan_number}</Td>
                  <Td className="text-slate-600">{l.property_address}{l.lender && <div className="text-xs text-slate-400">via {l.lender}</div>}</Td>
                  <Td className="font-mono text-xs text-slate-500">{l.loan_date}</Td>
                  <Td>
                    <span className={`overline ${(l.loan_type === "interest_only") ? "text-amber-700" : "text-slate-500"}`}>
                      {l.loan_type === "interest_only" ? "Interest-only" : "Amortizing"}
                    </span>
                    {l.io_period_months > 0 && l.loan_type === "interest_only" && (
                      <div className="text-[10px] text-slate-400 font-mono">IO {l.io_period_months}m</div>
                    )}
                  </Td>
                  <Td className="text-right font-mono font-bold">{currency(l.loan_amount)}</Td>
                  <Td className="text-right font-mono">{l.interest_rate}%</Td>
                  <Td className="text-right font-mono">{l.term_years}y</Td>
                  <Td className="text-right font-mono font-bold text-slate-900">{currency(l.monthly_payment)}</Td>
                  <Td className="text-right font-mono text-amber-700">{currency(l.total_interest)}</Td>
                  <Td className="text-right font-mono text-slate-700">{currency(l.total_paid)}</Td>
                  <Td className="text-right font-mono">{l.balloon_payment > 0 ? currency(l.balloon_payment) : "—"}</Td>
                </tr>
              ))}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr className="bg-slate-900 text-white" data-testid="loan-report-totals">
                  <Td colSpan={4} className="font-bold">Portfolio Totals</Td>
                  <Td className="text-right font-mono font-bold">{currency(totals.principal)}</Td>
                  <Td className="text-right font-mono font-bold">{weightedRate.toFixed(2)}%</Td>
                  <Td>—</Td>
                  <Td className="text-right font-mono font-bold">{currency(totals.monthly)}</Td>
                  <Td className="text-right font-mono font-bold text-amber-300">{currency(totals.interest)}</Td>
                  <Td className="text-right font-mono font-bold">{currency(totals.total_paid)}</Td>
                  <Td className="text-right font-mono font-bold">{currency(totals.balloon)}</Td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Taxes Due Report ---------------- */
function TaxesDueReport() {
  const [start, setStart] = useState(`${new Date().getFullYear()}-01-01`);
  const [end, setEnd] = useState(`${new Date().getFullYear()}-12-31`);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [accruingOnly, setAccruingOnly] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/reports/taxes-due", {
        params: { start_date: start, end_date: end },
      });
      setData(data);
    } catch (e) {
      const msg = e.response?.data?.detail || "Failed to load report";
      toast.error(typeof msg === "string" ? msg : "Failed to load report");
      setData(null);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const downloadPdfTaxes = async () => {
    try {
      const suffix = accruingOnly ? "_accruing-only" : "";
      const params = { start_date: start, end_date: end };
      if (accruingOnly) params.accruing_only = "true";
      await authenticatedDownload(
        "/api/reports/taxes-due/pdf",
        `taxes-due_${start}_to_${end}${suffix}.pdf`,
        params,
      );
    } catch {
      toast.error("PDF download failed");
    }
  };

  const statusBadge = (status, daysLeft) => {
    if (status === "overdue") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-widest bg-red-600 text-white">
          {Math.abs(daysLeft)}d overdue
        </span>
      );
    }
    if (status === "due_soon") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-widest bg-amber-100 text-amber-800 border border-amber-300">
          {daysLeft === 0 ? "Due today" : `${daysLeft}d left`}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-widest bg-slate-100 text-slate-700 border border-slate-200">
        {daysLeft}d left
      </span>
    );
  };

  const rowBg = (status) =>
    status === "overdue" ? "bg-red-50/60"
      : status === "due_soon" ? "bg-amber-50/40"
      : "";

  // Late fees are actively accruing once today is on or past the accrual date.
  const lateFeesAccruing = (iso) => {
    if (!iso) return false;
    const d = new Date(iso + "T00:00:00");
    if (Number.isNaN(d.getTime())) return false;
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t >= d;
  };

  return (
    <div data-testid="taxes-due-report">
      <div className="bg-white border border-slate-200 rounded-sm p-5 mb-6 no-print">
        <div className="grid md:grid-cols-4 gap-4">
          <div>
            <label className="overline text-slate-600 flex items-center gap-1.5"><Calendar className="w-3 h-3" /> Start date</label>
            <USDateInput data-testid="taxes-due-start-date" className="input-clean mt-2 w-full" value={start} onChange={(iso) => setStart(iso)} />
          </div>
          <div>
            <label className="overline text-slate-600 flex items-center gap-1.5"><Calendar className="w-3 h-3" /> End date</label>
            <USDateInput data-testid="taxes-due-end-date" className="input-clean mt-2 w-full" value={end} onChange={(iso) => setEnd(iso)} />
          </div>
          <div className="flex items-end">
            <button onClick={load} disabled={busy} data-testid="generate-taxes-due" className="btn-primary-dark w-full disabled:opacity-60">
              {busy ? "Generating…" : "Generate"}
            </button>
          </div>
          <div className="flex items-end gap-2">
            {[
              ["This year", `${new Date().getFullYear()}-01-01`, `${new Date().getFullYear()}-12-31`],
              ["Next 90d", today(), new Date(Date.now() + 90 * 864e5).toISOString().slice(0, 10)],
            ].map(([label, s, e]) => (
              <button
                key={label}
                onClick={() => { setStart(s); setEnd(e); setTimeout(load, 0); }}
                data-testid={`taxes-due-preset-${label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 px-3 py-1.5 rounded-sm transition-colors"
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-slate-100">
          <button
            onClick={downloadPdfTaxes}
            disabled={!data}
            data-testid="download-taxes-due-pdf"
            className="btn-primary-dark flex items-center gap-2 text-sm disabled:opacity-60"
            title={
              accruingOnly
                ? "Download PDF filtered to only properties accruing late fees"
                : "Download a printable Property Taxes Due report PDF for this date range"
            }
          >
            <Download className="w-4 h-4" />
            {accruingOnly ? "Download PDF (accruing only)" : "Download PDF"}
          </button>
        </div>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6" data-testid="taxes-due-summary">
          <Summary label="Properties in window" value={data.totals.count} tone="yellow" />
          <Summary label="Overdue" value={data.totals.overdue} tone="red" />
          <Summary label="Due within 30 days" value={data.totals.due_soon} tone="purple" />
          <button
            type="button"
            onClick={() => {
              if ((data.totals.late_fees_accruing ?? 0) === 0) return;
              setAccruingOnly((v) => !v);
            }}
            disabled={(data.totals.late_fees_accruing ?? 0) === 0}
            data-testid="kpi-late-fees-accruing"
            aria-pressed={accruingOnly}
            title={(data.totals.late_fees_accruing ?? 0) === 0
              ? "No properties are accruing late fees right now"
              : accruingOnly
                ? "Click to clear filter"
                : "Click to filter table to accruing properties only"}
            className={`text-left transition-all rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400 ${
              (data.totals.late_fees_accruing ?? 0) === 0 ? "cursor-default" : "cursor-pointer"
            } ${accruingOnly ? "ring-2 ring-red-500 shadow-[0_0_0_4px_rgba(254,202,202,0.45)]" : ""}`}
          >
            <Summary
              label={accruingOnly ? "Late fees accruing · filtered" : "Late fees accruing"}
              value={data.totals.late_fees_accruing ?? 0}
              tone="red"
            />
          </button>
          <Summary label="Total annual taxes" value={currency(data.totals.total_annual_taxes)} tone="red" />
        </div>
      )}

      {data && accruingOnly && (
        <div
          data-testid="taxes-due-accruing-filter-banner"
          className="flex items-center justify-between gap-3 mb-4 px-4 py-2.5 bg-red-50 border border-red-200 rounded-sm text-sm text-red-800"
        >
          <span>
            Showing only properties with late fees actively accruing (
            <b className="font-mono">{data.totals.late_fees_accruing ?? 0}</b>
            of <b className="font-mono">{data.rows.length}</b>).
          </span>
          <button
            type="button"
            onClick={() => setAccruingOnly(false)}
            data-testid="taxes-due-clear-accruing-filter"
            className="font-semibold text-red-700 hover:text-red-900 hover:underline"
          >
            Clear filter
          </button>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="taxes-due-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <Th>Property</Th>
              <Th>Address</Th>
              <Th className="whitespace-nowrap">Due Date</Th>
              <Th>Status</Th>
              <Th className="whitespace-nowrap">Late Fees Accrual</Th>
              <Th className="text-right whitespace-nowrap">Annual Taxes</Th>
            </tr>
          </thead>
          <tbody>
            {!data && (
              <tr><td colSpan={6} className="text-center py-16 text-slate-400">Pick a date range and generate.</td></tr>
            )}
            {data && data.rows.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-16 text-slate-400">
                  No properties with a tax-due date in this window. Add a <b>Taxes Due Date</b> on each property to populate this report.
                </td>
              </tr>
            )}
            {(() => {
              if (!data) return null;
              const visible = accruingOnly
                ? data.rows.filter((r) => lateFeesAccruing(r.late_fees_accrual_date))
                : data.rows;
              if (data.rows.length > 0 && visible.length === 0) {
                return (
                  <tr>
                    <td colSpan={6} className="text-center py-16 text-slate-400" data-testid="taxes-due-no-accruing">
                      No properties in this window are accruing late fees yet.
                    </td>
                  </tr>
                );
              }
              return visible.map((r) => (
                <tr
                  key={r.id}
                  className={`border-b border-slate-100 hover:bg-slate-50 ${rowBg(r.status)}`}
                  data-testid={`taxes-due-row-${r.id}`}
                >
                  <Td className="font-semibold text-slate-900">{r.name}</Td>
                  <Td className="text-slate-600">{r.address}</Td>
                  <Td className="font-mono whitespace-nowrap text-slate-700">{toUSDate(r.taxes_due_date)}</Td>
                  <Td>{statusBadge(r.status, r.days_left)}</Td>
                  <Td className={`font-mono whitespace-nowrap ${lateFeesAccruing(r.late_fees_accrual_date) ? "text-red-700 font-bold" : "text-slate-700"}`}>
                    {r.late_fees_accrual_date ? (
                      lateFeesAccruing(r.late_fees_accrual_date) ? (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-red-100 border border-red-300"
                          title="Late fees are actively accruing as of today"
                          data-testid={`late-fees-accruing-${r.id}`}
                        >
                          {toUSDate(r.late_fees_accrual_date)}
                        </span>
                      ) : (
                        toUSDate(r.late_fees_accrual_date)
                      )
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </Td>
                  <Td className="text-right font-mono font-bold whitespace-nowrap">{currency(r.annual_taxes)}</Td>
                </tr>
              ));
            })()}
          </tbody>
          {data && data.rows.length > 0 && (() => {
            const visible = accruingOnly
              ? data.rows.filter((r) => lateFeesAccruing(r.late_fees_accrual_date))
              : data.rows;
            if (visible.length === 0) return null;
            const subtotal = visible.reduce((s, r) => s + (r.annual_taxes || 0), 0);
            return (
              <tfoot>
                <tr className="bg-slate-900 text-white" data-testid="taxes-due-totals">
                  <Td colSpan={5} className="font-bold">{accruingOnly ? "Totals (accruing only)" : "Totals"}</Td>
                  <Td className="text-right font-mono font-bold whitespace-nowrap">{currency(subtotal)}</Td>
                </tr>
              </tfoot>
            );
          })()}
        </table>
      </div>
    </div>
  );
}



function Summary({ label, value, good, bad, tone }) {
  const tones = {
    yellow: { box: "bg-amber-50 border-amber-200", label: "text-amber-800", value: "text-amber-900" },
    red:    { box: "bg-red-50 border-red-200",     label: "text-red-700",   value: "text-red-700" },
    purple: { box: "bg-purple-50 border-purple-200", label: "text-purple-700", value: "text-purple-700" },
    blue:   { box: "bg-blue-50 border-blue-200",   label: "text-blue-700",  value: "text-blue-700" },
    green:  { box: "bg-emerald-50 border-emerald-200", label: "text-emerald-700", value: "text-emerald-700" },
  };
  const t = tones[tone];
  const boxCls = t ? t.box : "bg-white border-slate-200";
  const labelCls = t ? t.label : "text-slate-500";
  const valueCls = t ? t.value : (good ? "text-green-600" : bad ? "text-red-600" : "text-slate-900");
  return (
    <div className={`border rounded-sm p-5 ${boxCls}`}>
      <div className={`overline ${labelCls}`}>{label}</div>
      <div className={`mt-3 text-3xl font-bold tracking-tight font-mono ${valueCls}`}>
        {value}
      </div>
    </div>
  );
}

function Th({ children, className="" }) { return <th className={`px-5 py-3 overline text-slate-600 ${className}`}>{children}</th>; }
function Td({ children, className="", colSpan }) { return <td colSpan={colSpan} className={`px-5 py-4 ${className}`}>{children}</td>; }
